import { FormApi } from 'final-form'
import React from 'react'
import { Field } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'

import { Tertiary } from '@cinch/oxd-components/button/src'
import Select from '@cinch/oxd-components/field-select/src'
import Slider from '@cinch/oxd-components/filter-range/src'
import { Cross, Plus } from '@cinch/oxd-components/icons/src'
import Panel from '@cinch/oxd-components/panel/src'
import SVG from '@cinch/oxd-components/svg/src'
import {
  ADULT_AGE_GROUPS,
  CHILD_AGE_GROUPS,
  DOG_SIZES,
  HEIGHT_GROUPS,
  PASSENGER_TYPE,
} from '@cinch/oxd-pages/lifestyle/src/constants'
import { setToInt } from '@cinch/oxd-utils/form/src'

import * as styles from './passengers.module.css'

const KEY = 'passengers'
const heightValue = (value: number) => HEIGHT_GROUPS[value]
const sizeValue = (value: number) => DOG_SIZES[value]

export interface Props {
  form: FormApi
  i18n: Cinch.WebAppTranslations['lifestyle']['passengers']
}

const MAX_PASSENGERS_ALLOWED = 10

const humanAgeOptions = (isAdult: boolean) =>
  (isAdult ? ADULT_AGE_GROUPS : CHILD_AGE_GROUPS).reduce(
    (acc: any[], curr: string, idx: number) => [...acc, { value: idx, label: curr }],
    [],
  )

const PassengerFields: React.FC<Props> = ({ form, i18n }) => {
  const passengerType = (ind: number): 'adult' | 'child' | 'dog' | null => {
    let value: any = form.mutators.getValue(`${KEY}[${ind}].type`)
    // typecheck for backwards compatibility
    value =
      typeof value === 'string' && isNaN(parseInt(value, 10))
        ? (PASSENGER_TYPE.findIndex((kind) => kind.toLowerCase() === (value as string)) as number)
        : parseInt(value, 10)

    return value >= 0 ? (PASSENGER_TYPE[value].toLowerCase() as 'adult' | 'child' | 'dog') : null
  }
  const [passengerAmount, setPassengersAmount] = React.useState(0)
  const hasMaxPassengersBeenAdded = passengerAmount >= MAX_PASSENGERS_ALLOWED

  const unsubscribe = form.subscribe(
    ({ values }) => {
      if (!values.passengers || values.passengers.length === passengerAmount) {
        return
      }
      setPassengersAmount(values.passengers.length)
    },
    { values: true },
  )

  React.useEffect(() => () => unsubscribe())

  return (
    <>
      <FieldArray name={KEY}>
        {({ fields }) =>
          fields.map((name, index) => (
            <div key={name} className={styles.passenger} data-testid="passenger">
              <Panel className={styles.panel} isPadded={false}>
                <div className={styles.row}>
                  <Tertiary
                    type="button"
                    size={'mini'}
                    className={styles.remove}
                    onClick={() => form.mutators.remove(KEY, index)}
                    title={i18n.remove}
                  >
                    <SVG size={22} children={Cross} />
                  </Tertiary>
                  <Field
                    name={`${name}.type`}
                    render={(props) => (
                      <Select
                        id={props.input.name}
                        label={i18n.type.label}
                        options={PASSENGER_TYPE.map((label, index) => ({
                          value: index,
                          label,
                        }))}
                        oxford
                        {...props}
                      />
                    )}
                  />
                  {passengerType(index) === 'dog' ? (
                    <Field
                      name={`${name}.height`}
                      parse={setToInt}
                      component={Slider}
                      label={i18n.sizeOfDog}
                      min={0}
                      max={DOG_SIZES.length - 1}
                      valueFormatter={sizeValue}
                      step={1}
                    />
                  ) : (
                    <>
                      <Field
                        name={`${name}.age`}
                        format={setToInt}
                        render={(props) => (
                          <Select
                            id={`${name}.age`}
                            label={i18n.ageOfHuman}
                            options={humanAgeOptions(passengerType(index) === 'adult')}
                            oxford
                            {...props}
                          />
                        )}
                      />
                      <Field
                        name={`${name}.height`}
                        parse={setToInt}
                        component={Slider}
                        label={i18n.heightOfHuman}
                        min={0}
                        max={HEIGHT_GROUPS.length - 1}
                        valueFormatter={heightValue}
                        step={1}
                      />
                    </>
                  )}
                </div>
              </Panel>
            </div>
          ))
        }
      </FieldArray>
      <div className={styles.buttons}>
        <Tertiary
          type="button"
          onClick={() => form.mutators.push(KEY, { type: PASSENGER_TYPE.indexOf('Adult'), age: 0, height: 1, size: 1 })}
          title={i18n.add}
          icon={Plus}
          disabled={hasMaxPassengersBeenAdded}
          data-testid="add-passenger"
        >
          Add passenger
        </Tertiary>
        {hasMaxPassengersBeenAdded && (
          <p className={styles.maxMessage}>You have added the maximum number of passengers possible</p>
        )}
      </div>
    </>
  )
}

export default PassengerFields
